import config from '#/config';
import { trackEventTyped } from '#/lib/analytics';
import {
  clearGlobalEnforcedChainId,
  globalEnforceChainId,
} from '#/lib/ethereum/chains';
import { signAndAssociateWalletWithModal } from '#/lib/ethereum/wallets/assignWallets';
import {
  decryptWallet,
  encryptWallet,
  UNLOCK_WALLET_SECRET,
} from '#/lib/ethereum/wallets/privateKeyWallet';
import { waitAsync } from '#/lib/promiseTools';
import { redirectTo } from '#/lib/redirect';
import { logoutAndRemoveWallet } from '#/reduxState/currentUser/currentUserUtils';
import { applyAndDispatchReferralCode } from '#/reduxState/currentUserData/userReferralUtils';
import { setDelegatedKey } from '#/reduxState/delegatedKey/authorizationReducer';
import { getStateOutsideOfComponent, staticDispatch } from '#/reduxState/store';
import {
  changePrivateKeyWallet,
  changeSoftwareDiscoveredWallet,
  changeSoftwareWallet,
  type IWallet,
} from '#/reduxState/wallet/walletReducer';

type AfterUnlockParams = {
  afterUnlockCallback?: () => void;
  redirectTo: string | undefined;
  promotions?: {
    referralCode: string | undefined;
    welcomeBonusCode: string | undefined;
  };
};

const handleAfterUnlock = async ({
  afterUnlockCallback,
  redirectTo: redirectionTo,
}: AfterUnlockParams) => {
  setTimeout(() => {
    if (afterUnlockCallback) {
      afterUnlockCallback();
    }

    if (redirectionTo) {
      redirectTo(redirectionTo);
    }
  }, 600);
};

const formatWalletType = (wallet: IWallet) =>
  wallet.walletType === 'softwareDiscovered'
    ? `${wallet.walletType}_${wallet.walletDiscoveryRdns}`
    : wallet.walletType;

export const handleUnlock = async (
  wallet: IWallet,
  delegatedKeyType: false | 'session' | 'persistent',
  afterUnlockParams: AfterUnlockParams,
  keepWalletUntilSuccess: boolean
) => {
  trackEventTyped({
    event: 'wallet_unlock_attempt',
    walletType: formatWalletType(wallet),
    walletDiscoveryRdns: wallet.walletDiscoveryRdns,
  });

  const { walletAddressOriginal } = wallet;

  // With new authentication (wallet) based model, we have to always sign and assign wallet
  // to get fresh wallet auth token

  // const walletWasLinked = await isWalletLinked(wallet);
  // if (!walletWasLinked) {

  // Before assigning new wallet, we need to first remove existing wallet.
  // In special cases like DelegatedKey renew, we want to keep it in case of failure.
  if (!keepWalletUntilSuccess) {
    // Fully wipe existing wallet session
    // - Without clearing it, there would be issues with some wallet specific checks like getting welcome bonus reedeem state
    const state = await getStateOutsideOfComponent();
    if (state.wallet.walletAddress) {
      await logoutAndRemoveWallet({
        currentUser: state.currentUser,
        walletType: wallet.walletType,
      });
    }
    // staticDispatch(removeWallet());
    // await waitAsync(30); // Make sure it's propagated in Redux store :o
  }

  // Unlock is only allowed on main chain
  globalEnforceChainId(config.chains.katanaChain.chainId);

  const { personalWalletSecret, delegatedKey } =
    await signAndAssociateWalletWithModal(wallet, !!delegatedKeyType).finally(
      () => {
        clearGlobalEnforcedChainId();
      }
    );

  // Apply promotions (and remove from storage)
  const referralCode = afterUnlockParams.promotions?.referralCode;
  if (referralCode) {
    applyAndDispatchReferralCode(wallet.walletAddress, referralCode);
  }

  switch (wallet.walletType) {
    case 'softwareDiscovered_metaMask':
    case 'softwareDiscovered_trustWallet':
    case 'softwareDiscovered':
      staticDispatch(
        changeSoftwareDiscoveredWallet({
          wallet: walletAddressOriginal,
          walletType: 'softwareDiscovered',
          walletDiscoveredRdns: wallet.walletDiscoveryRdns,
          personalWalletSecret: wallet.personalWalletSecret,
        })
      );
      break;
    case 'keystore':
    case 'privateKey': {
      // We need to decrypt initially encrypted wallet (used for associate wallet signature)
      const decrypted = await decryptWallet(
        wallet.privateKeyEncrypted,
        UNLOCK_WALLET_SECRET
      );

      const reEncrypted = await encryptWallet(decrypted, personalWalletSecret);
      staticDispatch(
        changePrivateKeyWallet({
          wallet: walletAddressOriginal,
          privateKeyEncrypted: reEncrypted,
          personalWalletSecret,
          unlockedByKeystoreFile: wallet.walletType === 'keystore',
        })
      );
      break;
    }
    // case 'ledger':
    // case 'trezor':
    //   staticDispatch(
    //     changeHardwareWallet({
    //       wallet: walletAddressOriginal,
    //       path: wallet.hardwareWalletPath,
    //       walletType: wallet.walletType,
    //       personalWalletSecret,
    //     })
    //   );
    //   break;
    case 'softwareWeb3':
    case 'coinbaseWallet':
    case 'walletConnect':
      staticDispatch(
        changeSoftwareWallet({
          wallet: walletAddressOriginal,
          walletType: wallet.walletType,
          personalWalletSecret,
        })
      );
      break;
    default:
      throw new Error('Unimplemented wallet');
  }

  await waitAsync(30); // Make sure it's propagated in Redux store :o

  if (delegatedKeyType && delegatedKey) {
    staticDispatch(setDelegatedKey(delegatedKeyType, delegatedKey));
  }

  await handleAfterUnlock(afterUnlockParams);

  trackEventTyped({
    event: 'wallet_unlock_success',
    walletType: formatWalletType(wallet),
    walletDiscoveryRdns: wallet.walletDiscoveryRdns,
  });
};
